import { useEffect, useState } from 'react';

// Shared display-unit store for the Budget Overview screen.
//
// The m / k / Full toggle lives in the Overview table controls, but the unit it
// selects must also drive the Financial Summary cards — which mount as a SEPARATE
// React root. A module-level store plus a window CustomEvent keeps both roots in
// sync without a shared provider. State is persisted per-browser in localStorage
// (session-level persistence is acceptable for v1, see the ticket).

export const DISPLAY_UNIT = {
    M: 'm',
    K: 'k',
    FULL: 'full',
};

const STORAGE_KEY = 'ib_overview_display_unit';
const EVENT_NAME = 'ib:overview-display-unit';
const DEFAULT_UNIT = DISPLAY_UNIT.M;
const VALID_UNITS = [DISPLAY_UNIT.M, DISPLAY_UNIT.K, DISPLAY_UNIT.FULL];

export function getDisplayUnit() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return VALID_UNITS.indexOf(stored) !== -1 ? stored : DEFAULT_UNIT;
    } catch (e) {
        return DEFAULT_UNIT;
    }
}

export function setDisplayUnit(unit) {
    if (VALID_UNITS.indexOf(unit) === -1) {
        return;
    }

    try {
        localStorage.setItem(STORAGE_KEY, unit);
    } catch (e) {
        /* localStorage unavailable — fall back to the in-memory broadcast only */
    }

    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: unit }));
}

// Subscribes a component to the shared unit. Initial value comes from localStorage;
// the component re-renders whenever any root broadcasts a change.
export function useDisplayUnit() {
    const [unit, setUnit] = useState(getDisplayUnit);

    useEffect(() => {
        const handler = (event) => {
            const next = event && event.detail;
            setUnit(VALID_UNITS.indexOf(next) !== -1 ? next : getDisplayUnit());
        };

        window.addEventListener(EVENT_NAME, handler);
        return () => window.removeEventListener(EVENT_NAME, handler);
    }, []);

    return unit;
}
